import React, { useState, useEffect, useRef } from 'react';

// ─── Figma source: node #467:399 — LANDING PAGE_DESKTOP (1440×1024) ───────────
// File key: WmN2tKfRSxv1PAWTdQBkrV
// All positions/dimensions are exact Figma values — no interpretation.

interface LandingPageDesktopProps {
  onChooseChordGrid: () => void;
  onChoosePianoRec: () => void;
  onOpen: () => void;
  onAccount: () => void;   // header-icon-1 = bonhomme #476:530
  onSettings: () => void;  // header-icon-2 = engrenage #482:3794
  onHelp?: () => void;     // header-icon-3 = ? #482:3797
  onSetlists?: () => void;
  onJamSession?: () => void;
}

// ── Translation table (Figma → CSS) ──────────────────────────────────────────
// Frame #467:399 root | backgroundColor fill #231E1B
// Logo #468:403 | locationRelativeToParent x:54 y:40 | dimensions 161×44.15
// Nav NEW #469:478 | x:368 y:40 h:34.65 | text #469:440 "NEW" style_a1506bb5 | icon #491:82 34.65×34.65 fill #DFCC21
// Nav OPEN #469:479 | x:534 y:40 h:35 | text #469:439 "OPEN" style_a1506bb5 | icon #491:85 35×35 fill #DB9817
// Nav SETLISTS #470:488 | x:710 y:40 | text #469:441 "SETLISTS" style_a1506bb5 | icon #491:90 29×29 fill #D4A853
// Nav ONLINE LIBRARY #520:559→#469:442 | x:954 y:40 | text "ONLINE LIBRARY" style_a1506bb5
// Right icon 1 #476:530 | x:1264 y:43.33 | 22.33×22.33 | bonhomme → onAccount
// Right icon 2 #482:3794 | x:1313.66 y:43.33 | 22.33×22.33 | engrenage → onSettings
// Right icon 3 #482:3797 | x:1363 y:43.33 | 22.33×22.33 | ? → onHelp
// style_a1506bb5 | Kumbh Sans Medium 500 20px lh1.25em textCase SMALL_CAPS LEFT fill #D3CCBD
// Hero card #502:355 | x:54 y:159 | 1331×796 | borderRadius 44px | boxShadow 6px 4px 20.7px 15px rgba(0,0,0,0.25)
// style_d91fd525 | Kumbh Sans Black 900 64px lh1.25em textCase SMALL_CAPS CENTER fill #D3CCBD
// style_8c827b22 | Kumbh Sans Medium 500 36px lh1.25em textCase SMALL_CAPS CENTER fill #D3CCBD
// SLIDE bg images — position absolute with exact Figma locationRelativeToParent + dimensions
//   opacity on RECTANGLE node = CSS opacity on <img>
//   scaleMode FILL → objectFit:'cover' | scaleMode STRETCH → objectFit:'fill'
//   fills order in Figma: [top fill, bottom fill] → render image first, overlay second
// Dots | ELLIPSE 16×16 | y:727 | x:593/625/657/689/721 | active fill #53D2D4 | inactive fill #D3CCBD
// ─────────────────────────────────────────────────────────────────────────────

interface SlideBg {
  src: string;
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
  objectFit: 'cover' | 'fill'; // FILL→cover | STRETCH→fill
  overlay?: string;             // second fill on top of image (slide 2)
}

interface SlideData {
  id: number;
  bgColor: string;
  bg?: SlideBg;
  headline: string;
  headlineBox: { x: number; y: number; width: number };
  sub: string;
  subBox: { x: number; y: number; width: number };
  showDevices?: boolean;
}

// Exact Figma values — all strings from Figma TEXT nodes, all coordinates from locationRelativeToParent
const SLIDES: SlideData[] = [
  {
    // COMPONENT "SLIDE=1" #502:349 | bgColor fill_cf0c38bb #3D3028
    id: 0,
    bgColor: '#3D3028',
    // RECTANGLE "band-rehearsal 1" #502:314 | locationRelativeToParent x:-115 y:-30 | dimensions 1558×838
    // fills IMAGE scaleMode FILL → objectFit cover | opacity 0.3499999940395355
    bg: { src: '/band-rehearsal.jpg', x: -115, y: -30, width: 1558, height: 838, opacity: 0.35, objectFit: 'cover' },
    // TEXT #502:328 | locationRelativeToParent x:145 y:122 | dimensions 1040×276 | style_d91fd525
    headline: 'Your professional\nchord chart companion.\nCreate, edit, share and perform.',
    headlineBox: { x: 145, y: 122, width: 1040 },
    // TEXT #502:327 | locationRelativeToParent x:446 y:510 | dimensions 439×45 | style_8c827b22
    sub: 'All Device Access anytime',
    subBox: { x: 446, y: 510, width: 439 },
    // IMAGE-SVG #502:315 | locationRelativeToParent x:557 y:584.08 | dimensions 216.47×48.92
    showDevices: true,
  },
  {
    // COMPONENT "SLIDE=2" #502:346 | bgColor fill_cf0c38bb #3D3028
    id: 1,
    bgColor: '#3D3028',
    // RECTANGLE "editor" #502:330 | locationRelativeToParent x:369 y:234 | dimensions 593×319
    // fills [rgba(0,0,0,0.2) top, IMAGE scaleMode STRETCH bottom] | no layer opacity
    bg: { src: '/figma-assets/landing-desktop/bg-editor-5cd263.png', x: 369, y: 234, width: 593, height: 319, opacity: 1, objectFit: 'fill', overlay: 'rgba(0,0,0,0.2)' },
    // TEXT #502:331 | locationRelativeToParent x:182 y:28 | dimensions 968×192 | style_d91fd525
    headline: 'Chord notation made simple.\nSetlist management made easy.',
    headlineBox: { x: 182, y: 28, width: 968 },
    // TEXT #502:332 | locationRelativeToParent x:465 y:595 | dimensions 402×90 | style_8c827b22
    sub: 'Focus on you music,\nenjoy you performance.',
    subBox: { x: 465, y: 595, width: 402 },
  },
  {
    // COMPONENT "SLIDE=3" #502:348 | bgColor fill_cf0c38bb #3D3028
    id: 2,
    bgColor: '#3D3028',
    // RECTANGLE "midi-connection 1" #502:334 | locationRelativeToParent x:-106 y:-19 | dimensions 1559×856
    // fills IMAGE scaleMode FILL → objectFit cover | opacity 0.2800000011920929
    bg: { src: '/midi-connection.jpg', x: -106, y: -19, width: 1559, height: 856, opacity: 0.28, objectFit: 'cover' },
    // TEXT #502:335 | layout_23db6560 x:182 y:130 | dimensions 968×330 | style_d91fd525
    // Figma string: NO line break — single paragraph
    headline: 'Write easily with the Chord Palette or connect any MIDI keyboard and play.',
    headlineBox: { x: 182, y: 130, width: 968 },
    // TEXT #502:336 | locationRelativeToParent x:484 y:582 | dimensions 365×45 | style_8c827b22
    sub: 'Fast and pro notation.',
    subBox: { x: 484, y: 582, width: 365 },
  },
  {
    // COMPONENT "SLIDE=4" #502:345 | bgColor #472D2E (different from others)
    id: 3,
    bgColor: '#472D2E',
    // RECTANGLE "JamSession 1" #502:338 | locationRelativeToParent x:-115 y:-23 | dimensions 1537×832
    // fills IMAGE scaleMode FILL → objectFit cover | opacity 0.25
    bg: { src: '/JamSession.jpg', x: -115, y: -23, width: 1537, height: 832, opacity: 0.25, objectFit: 'cover' },
    // TEXT #502:339 | locationRelativeToParent x:275 y:130 | dimensions 758×330 | style_d91fd525
    headline: 'Jam Sessions :\ncollaborate in real-time,\nanywhere.',
    headlineBox: { x: 275, y: 130, width: 758 },
    // TEXT #502:340 | locationRelativeToParent x:135 y:504 | dimensions 1061×90 | style_8c827b22
    sub: 'Invite other musicians to join. Share chord charts and setlist in real-time, and play together from anywhere in the world.',
    subBox: { x: 135, y: 504, width: 1061 },
  },
  {
    // COMPONENT "SLIDE=5" #502:347 | bgColor fill_cf0c38bb #3D3028
    id: 4,
    bgColor: '#3D3028',
    // RECTANGLE "live-performance 1" #502:342 | locationRelativeToParent x:-92 y:-36 | dimensions 1755×953
    // fills IMAGE scaleMode FILL → objectFit cover | opacity 0.75
    bg: { src: '/live-performance.jpg', x: -92, y: -36, width: 1755, height: 953, opacity: 0.75, objectFit: 'cover' },
    // TEXT #502:343 | layout_23db6560 x:182 y:130 | dimensions 968×330 | style_d91fd525
    headline: 'Perform Live\nGig mode for seamless live performance',
    headlineBox: { x: 182, y: 130, width: 968 },
    // TEXT #502:344 | locationRelativeToParent x:136 y:548 | dimensions 1061×45 | style_8c827b22
    sub: 'Navigate set lists, library, and focus on your music.',
    subBox: { x: 136, y: 548, width: 1061 },
  },
];

// Dot x positions from Figma (relative to hero card): 593, 625, 657, 689, 721
const DOT_X = [593, 625, 657, 689, 721];
const DOT_Y = 727;

// Nav label style — style_a1506bb5: Kumbh Sans 500 20px SMALL_CAPS LEFT #D3CCBD
const navLabel: React.CSSProperties = {
  fontFamily: 'Kumbh Sans, sans-serif',
  fontWeight: 500,
  fontSize: 20,
  lineHeight: '1.25em',
  fontVariantCaps: 'small-caps',
  color: '#D3CCBD',
  whiteSpace: 'nowrap',
};

// Headline style — style_d91fd525
// textCase SMALL_CAPS: Kumbh Sans has no native small-caps OpenType glyphs.
// Figma renders it as normal mixed-case (lowercase letterforms at lowercase height).
// CSS font-variant-caps: small-caps produces uppercase letterforms at lowercase height — WRONG.
const headlineStyle: React.CSSProperties = {
  fontFamily: 'Kumbh Sans, sans-serif',
  fontWeight: 900,
  fontSize: 64,
  lineHeight: '1.25em',
  textAlign: 'center',
  color: '#D3CCBD',
  whiteSpace: 'pre-line',
};

// Sub style — style_8c827b22
const subStyle: React.CSSProperties = {
  fontFamily: 'Kumbh Sans, sans-serif',
  fontWeight: 500,
  fontSize: 36,
  lineHeight: '1.25em',
  textAlign: 'center',
  color: '#D3CCBD',
  whiteSpace: 'pre-line',
};

const LandingPageDesktop: React.FC<LandingPageDesktopProps> = ({
  onChooseChordGrid,
  onChoosePianoRec,
  onOpen,
  onAccount,
  onSettings,
  onHelp,
  onSetlists,
  onJamSession,
}) => {
  const [slide, setSlide] = useState(0);
  // autoKey resets the auto-advance timer when user manually navigates
  const [autoKey, setAutoKey] = useState(0);
  // New-chart dropdown (replaces the old modal for this entry point)
  const [isNewDropdownOpen, setIsNewDropdownOpen] = useState(false);
  const newDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isNewDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (newDropdownRef.current && !newDropdownRef.current.contains(e.target as Node)) {
        setIsNewDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNewDropdownOpen]);

  // Scale the 1440×1024 frame to fit the viewport (letterbox with #231E1B bg)
  const computeScale = () =>
    Math.min(window.innerWidth / 1440, window.innerHeight / 1024);

  const [scale, setScale] = useState(computeScale);

  useEffect(() => {
    const update = () => setScale(computeScale());
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Auto-advance every 5 seconds; resets on manual dot click
  useEffect(() => {
    const timer = setInterval(() => setSlide(s => (s + 1) % 5), 5000);
    return () => clearInterval(timer);
  }, [autoKey]);

  const goToSlide = (i: number) => {
    setSlide(i);
    setAutoKey(k => k + 1);
  };

  const offsetX = (window.innerWidth - 1440 * scale) / 2;
  const offsetY = (window.innerHeight - 1024 * scale) / 2;

  return (
    // Full-screen overlay — same z-index level as WelcomeScreen (handled by parent in App.tsx)
    <div style={{ width: '100%', height: '100%', backgroundColor: '#231E1B', overflow: 'hidden', position: 'relative' }}>
      {/* Scaled 1440×1024 Figma frame */}
      <div
        style={{
          position: 'absolute',
          width: 1440,
          height: 1024,
          left: offsetX,
          top: offsetY,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          backgroundColor: '#231E1B',
        }}
      >
        {/* ── HEADER ── */}
        {/* Logo #468:403 | x:54 y:40 | 161×44 */}
        <img
          src="/figma-assets/landing-desktop/logo-header.svg"
          style={{ position: 'absolute', left: 54, top: 40, width: 161, height: 44, display: 'block' }}
          alt="Chord Notator"
        />

        {/* Nav NEW #534:614 | frame x:303 y:44 | layout row gap:13 | icon 34.65×34.65 | text 50×29 */}
        <div ref={newDropdownRef} style={{ position: 'absolute', left: 303, top: 44 }}>
          <button
            type="button"
            aria-label="New chart"
            aria-expanded={isNewDropdownOpen}
            style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 13, cursor: 'pointer', background: 'none', border: 'none', padding: 0, zIndex: 20 }}
            onClick={() => setIsNewDropdownOpen(v => !v)}
          >
            <img src="/figma-assets/landing-desktop/nav-new.svg" style={{ width: 34.65, height: 34.65, pointerEvents: 'none' }} alt="" />
            <span style={{ ...navLabel, width: 50, height: 29, pointerEvents: 'none' }}>NEW</span>
          </button>

          {isNewDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: 8,
                width: 190,
                backgroundColor: '#161616',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                overflow: 'hidden',
                zIndex: 30,
              }}
            >
              <button
                type="button"
                onClick={() => { setIsNewDropdownOpen(false); onChooseChordGrid(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', fontSize: 14 }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10"></line>
                  <line x1="12" y1="20" x2="12" y2="4"></line>
                  <line x1="6" y1="20" x2="6" y2="14"></line>
                </svg>
                Chord Grid
              </button>
              <button
                type="button"
                onClick={() => { setIsNewDropdownOpen(false); onChoosePianoRec(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', fontSize: 14, borderTop: '1px solid rgba(255,255,255,0.08)' }}
              >
                <img src="/figma-assets/edit-mode/icon-piano-rec.svg" width="18" height="18" alt="" style={{ display: 'block' }} />
                Piano Rec
              </button>
            </div>
          )}
        </div>

        {/* Nav OPEN #469:479 | frame x:441 y:44 | layout row gap:13 | icon 35×35 | text 60×29 */}
        <button
          type="button"
          aria-label="Open"
          style={{ position: 'absolute', left: 441, top: 44, display: 'flex', alignItems: 'flex-start', gap: 13, cursor: 'pointer', background: 'none', border: 'none', padding: 0, zIndex: 20 }}
          onClick={onOpen}
        >
          <img src="/figma-assets/landing-desktop/nav-open.svg" style={{ width: 35, height: 35, pointerEvents: 'none' }} alt="" />
          <span style={{ ...navLabel, width: 60, height: 29, pointerEvents: 'none' }}>OPEN</span>
        </button>

        {/* Nav SETLISTS #470:488 | frame x:589 y:44 | layout row alignItems:center gap:15 | icon 29×29 | text 88×29 */}
        <button
          type="button"
          aria-label="Setlists"
          style={{ position: 'absolute', left: 589, top: 44, display: 'flex', alignItems: 'center', gap: 15, cursor: 'pointer', background: 'none', border: 'none', padding: 0, zIndex: 20 }}
          onClick={onSetlists}
        >
          <img src="/figma-assets/landing-desktop/nav-setlists.svg" style={{ width: 29, height: 29, pointerEvents: 'none' }} alt="" />
          <span style={{ ...navLabel, width: 88, height: 29, pointerEvents: 'none' }}>SETLISTS</span>
        </button>

        {/* Nav JAM SESSION #593:411 | frame x:761 y:42 | layout row alignItems:center gap:17 | icon #593:395 24.42×29 | text 143×34 */}
        <button
          type="button"
          aria-label="Jam session"
          style={{ position: 'absolute', left: 761, top: 42, display: 'flex', alignItems: 'center', gap: 17, cursor: 'pointer', background: 'none', border: 'none', padding: 0, zIndex: 20 }}
          onClick={onJamSession}
        >
          <img src="/figma-assets/landing-desktop/nav-jam-session.svg" style={{ width: 24.42, height: 29, pointerEvents: 'none' }} alt="" />
          <span style={{ ...navLabel, width: 143, height: 29, pointerEvents: 'none' }}>JAM SESSION</span>
        </button>

        {/* Right icon 1 #476:530 | x:1264 y:44.33 | 22.33×22.33 | bonhomme → account */}
        <button
          type="button"
          aria-label="Account"
          style={{ position: 'absolute', left: 1264, top: 44, width: 22, height: 22, cursor: 'pointer', background: 'none', border: 'none', padding: 0, zIndex: 20 }}
          onClick={onAccount}
        >
          <img
            src="/figma-assets/landing-desktop/header-icon-1.svg"
            style={{ position: 'absolute', left: 0, top: 0, width: 22, height: 22, pointerEvents: 'none' }}
            alt=""
          />
        </button>

        {/* Right icon 2 #482:3794 | x:1313.66 y:44.33 | 22.33×22.33 | engrenage → settings */}
        <button
          type="button"
          aria-label="Settings"
          style={{ position: 'absolute', left: 1314, top: 44, width: 22, height: 22, cursor: 'pointer', background: 'none', border: 'none', padding: 0, zIndex: 20 }}
          onClick={onSettings}
        >
          <img
            src="/figma-assets/landing-desktop/header-icon-2.svg"
            style={{ position: 'absolute', left: 0, top: 0, width: 22, height: 22, pointerEvents: 'none' }}
            alt=""
          />
        </button>

        {/* Right icon 3 #482:3797 | x:1363 y:44.33 | 22.33×22.33 | ? → help */}
        <button
          type="button"
          aria-label="Help"
          style={{ position: 'absolute', left: 1363, top: 44, width: 22, height: 22, cursor: 'pointer', background: 'none', border: 'none', padding: 0, zIndex: 20 }}
          onClick={onHelp}
        >
          <img
            src="/figma-assets/landing-desktop/header-icon-3.svg"
            style={{ position: 'absolute', left: 0, top: 0, width: 22, height: 22, pointerEvents: 'none' }}
            alt=""
          />
        </button>

        {/* ── HERO CARD #502:355 | x:54 y:159 | 1331×796 | borderRadius:44 ── */}
        <div
          style={{
            position: 'absolute',
            left: 54,
            top: 159,
            width: 1331,
            height: 796,
            borderRadius: 44,
            backgroundColor: '#3D3028',
            boxShadow: '6px 4px 20.7px 15px rgba(0,0,0,0.25)',
            overflow: 'hidden',
          }}
        >
          {/* Slide layers — all rendered, toggled via opacity for CSS cross-fade */}
          {SLIDES.map((s, i) => (
            <div
              key={s.id}
              style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: s.bgColor,
                opacity: i === slide ? 1 : 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: i === slide ? 'auto' : 'none',
              }}
            >
              {/* Background image — exact Figma locationRelativeToParent + dimensions + opacity */}
              {/* fills order: image bottom, overlay top (Figma fills array = [topFill, bottomFill]) */}
              {s.bg && (
                <>
                  <img
                    src={s.bg.src}
                    style={{
                      position: 'absolute',
                      left: s.bg.x,
                      top: s.bg.y,
                      width: s.bg.width,
                      height: s.bg.height,
                      maxWidth: 'none', // override Tailwind Preflight max-width:100%
                      objectFit: s.bg.objectFit,
                      opacity: s.bg.opacity,
                      display: 'block',
                    }}
                    alt=""
                  />
                  {s.bg.overlay && (
                    <div
                      style={{
                        position: 'absolute',
                        left: s.bg.x,
                        top: s.bg.y,
                        width: s.bg.width,
                        height: s.bg.height,
                        backgroundColor: s.bg.overlay,
                      }}
                    />
                  )}
                </>

              )}

              {/* Headline — style_d91fd525: Kumbh Sans 900 64px SMALL_CAPS CENTER */}
              <div
                style={{
                  ...headlineStyle,
                  position: 'absolute',
                  left: s.headlineBox.x,
                  top: s.headlineBox.y,
                  width: s.headlineBox.width,
                }}
              >
                {s.headline}
              </div>

              {/* Sub — style_8c827b22: Kumbh Sans 500 36px SMALL_CAPS CENTER */}
              <div
                style={{
                  ...subStyle,
                  position: 'absolute',
                  left: s.subBox.x,
                  top: s.subBox.y,
                  width: s.subBox.width,
                }}
              >
                {s.sub}
              </div>

              {/* Device icons (slide 1 only) — hero-devices.svg x:557 y:584 216×49 */}
              {s.showDevices && (
                <img
                  src="/figma-assets/landing-desktop/hero-devices.svg"
                  style={{ position: 'absolute', left: 557, top: 584, width: 217, height: 49, display: 'block' }}
                  alt=""
                />
              )}
            </div>
          ))}

          {/* Dot indicators — always above slides, z-index:10 */}
          {/* y:727 | x: 593/625/657/689/721 | active:#53D2D4 inactive:#D3CCBD | 16×16 */}
          {DOT_X.map((x, i) => (
            <div
              key={i}
              onClick={() => goToSlide(i)}
              style={{
                position: 'absolute',
                left: x,
                top: DOT_Y,
                width: 16,
                height: 16,
                borderRadius: 8,
                backgroundColor: i === slide ? '#53D2D4' : '#D3CCBD',
                cursor: 'pointer',
                zIndex: 10,
                transition: 'background-color 0.3s ease',
              }}
            />
          ))}
        </div>

      </div>
    </div>
  );
};

export default LandingPageDesktop;
